import { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	BellIcon,
	Button,
	CompassIcon,
	Container,
	ConversationIcon,
	DialogueIcon,
	FlexGap,
	FriendsFeminineMasculineIcon,
	GearIcon,
	GridIcon,
	GroupsIcon,
	HammerIcon,
	HeartIcon,
	HeartRateIcon,
	HistoryIcon,
	Jumbo,
	LibraryIcon,
	Modal,
	NetworkIcon,
	PaintBrushWideIcon,
	Popover,
	PuzzleIcon,
	SuitcaseIcon,
	tokens,
} from '@treams/treams-ui';

import { ConnectorErrorSummary } from '../connectors/ConnectorErrorSummary';
import WorkspaceFeatures from '../organization/settings/WorkspaceFeatures';
import OverviewSection from './OverviewSection';

type ItemsOptions = {
	hasCycles: boolean;
	hasFeedback: boolean;
	hasConversations: boolean;
	hasTalentReviews: boolean;
	hasLearning: boolean;
	hasEngagement: boolean;
};

const DefaultItems = (t: TFunction, options: ItemsOptions) => ({
	tipsntricks: [
		{
			icon: <HeartIcon />,
			link: 'https://login.circle.so/sign_in?request_host=community.ontreams.com#email',
			title: t('settings.community.title'),
		},
		{
			icon: <LibraryIcon />,
			title: t('settings.superuser.training'),
			link: t('settings.superuser.trainingLink'),
		},
	],
	organization: [
		{
			icon: <FriendsFeminineMasculineIcon />,
			link: '/settings/employees',
			title: t('settings.organizations.employees.title'),
		},
		{
			icon: <NetworkIcon />,
			link: '/settings/organization/departments',
			title: t('settings.organizations.departments.title'),
		},
		{
			icon: <GroupsIcon />,
			link: '/settings/organization/teams',
			title: t('settings.organizations.teams.title'),
		},
		{
			icon: <PaintBrushWideIcon />,
			link: '/settings/organization/theme',
			title: t('settings.organizations.theme.title'),
		},
		{
			icon: <PuzzleIcon />,
			link: '/settings/integrations',
			title: t('settings.integrations.title'),
		},
		{
			icon: <CompassIcon />,
			link: '/settings/organization/drivers',
			title: t('settings.organizations.drivers.title'),
		},
	],
	general: [
		{
			icon: <SuitcaseIcon />,
			link: '/settings/jobs',
			title: t('settings.jobs.title'),
		},
		{
			icon: <HistoryIcon />,
			link: '/settings/cycles',
			title: t('settings.cycles.title'),
			enabled: options.hasCycles,
		},
		{
			icon: <BellIcon />,
			link: '/settings/notifications',
			title: t('settings.notifications.title'),
		},
	],
	content: [
		{
			icon: <HammerIcon />,
			link: '/settings/competencies',
			title: t('settings.competencies.title'),
		},
		{
			icon: <DialogueIcon />,
			link: '/settings/feedback',
			title: t('settings.feedback.title'),
			enabled: options.hasFeedback,
		},
		{
			icon: <ConversationIcon />,
			link: '/settings/conversations',
			title: t('settings.conversations.title'),
			enabled: options.hasConversations,
		},
		{
			icon: <GridIcon />,
			link: '/settings/talent-reviews',
			title: t('settings.talentReviews.title'),
			enabled: options.hasTalentReviews,
		},
		{
			icon: <LibraryIcon />,
			link: '/settings/learning',
			title: t('settings.learning.title'),
			enabled: options.hasLearning,
		},
		{
			icon: <HeartRateIcon />,
			link: '/settings/engagement',
			title: t('settings.engagement.title'),
			enabled: options.hasEngagement,
		},
	],
});

const EngagementOfficerItems = (t: TFunction, options: ItemsOptions) => ({
	tipsntricks: [] as ReturnType<typeof DefaultItems>['tipsntricks'],
	organization: [] as ReturnType<typeof DefaultItems>['organization'],
	general: [] as ReturnType<typeof DefaultItems>['general'],
	content: [
		{
			icon: <HeartRateIcon />,
			link: '/settings/engagement',
			title: t('settings.engagement.title'),
			enabled: options.hasEngagement,
		},
	],
});

const HRPBItems = (t: TFunction, options: ItemsOptions) => {
	const items = DefaultItems(t, options);
	return {
		tipsntricks: items.tipsntricks,
		organization: items.organization.filter(
			(item) =>
				item.link !== '/settings/organization/theme' && item.link !== '/settings/integrations',
		),
		general: items.general.filter((item) => item.link !== '/settings/notifications'),
		content: items.content.filter((item) => item.link !== '/settings/engagement'),
	};
};

function OverviewSections(props: {
	tipsntricks: ReturnType<typeof DefaultItems>['tipsntricks'];
	organization: ReturnType<typeof DefaultItems>['organization'];
	general: ReturnType<typeof DefaultItems>['general'];
	content: ReturnType<typeof DefaultItems>['content'];
}) {
	const { t } = useTranslation();

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			{props.tipsntricks.length > 0 && (
				<OverviewSection
					title={t('settings.overview.tipsntricks.title')}
					description={t('settings.overview.tipsntricks.description')}
					tone="info"
					items={props.tipsntricks}
				/>
			)}
			{props.organization.length > 0 && (
				<OverviewSection
					title={t('settings.overview.organization.title')}
					description={t('settings.overview.organization.description')}
					tone="warning"
					items={props.organization}
				/>
			)}
			{props.general.length > 0 && (
				<OverviewSection
					title={t('settings.overview.general.title')}
					description={t('settings.overview.general.description')}
					tone="success"
					items={props.general}
				/>
			)}
			{props.content.length > 0 && (
				<OverviewSection
					title={t('settings.overview.content.title')}
					description={t('settings.overview.content.description')}
					tone="brand"
					items={props.content}
				/>
			)}
		</FlexGap>
	);
}

const EngagmentOfficerOverviewSettings = () => {
	const { t } = useTranslation();
	const hasEngagement = useFeature(graphql.FeatureName.Engagement);

	return (
		<OverviewSections
			{...EngagementOfficerItems(t, {
				hasCycles: false,
				hasLearning: false,
				hasFeedback: false,
				hasConversations: false,
				hasTalentReviews: false,
				hasEngagement,
			})}
		/>
	);
};

const HRPBOverviewSettings = () => {
	const { t } = useTranslation();
	const hasCycles = useFeature(graphql.FeatureName.Cycles);
	const hasLearning = useFeature(graphql.FeatureName.Learning);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasTalentReviews = useFeature(graphql.FeatureName.TalentReviews);
	const hasEngagement = useFeature(graphql.FeatureName.Engagement);

	return (
		<OverviewSections
			{...HRPBItems(t, {
				hasCycles,
				hasLearning,
				hasFeedback,
				hasConversations,
				hasTalentReviews,
				hasEngagement,
			})}
		/>
	);
};

const DefaultOverviewSettings = () => {
	const { t } = useTranslation();
	const hasCycles = useFeature(graphql.FeatureName.Cycles);
	const hasLearning = useFeature(graphql.FeatureName.Learning);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasTalentReviews = useFeature(graphql.FeatureName.TalentReviews);
	const hasEngagement = useFeature(graphql.FeatureName.Engagement);

	return (
		<OverviewSections
			{...DefaultItems(t, {
				hasCycles,
				hasLearning,
				hasFeedback,
				hasConversations,
				hasTalentReviews,
				hasEngagement,
			})}
		/>
	);
};

export default function OverviewSettings() {
	const { t } = useTranslation();
	const { isEngagementOfficer, isHRBusinessPartner } = useViewerRoles();
	const [edit, setEdit] = useState(false);
	const canEditModules = !isEngagementOfficer && !isHRBusinessPartner;

	return (
		<>
			<Jumbo
				title={t('settings.overview.title')}
				description={t('settings.overview.description')}
				button={
					canEditModules ? (
						<Popover.Target
							center
							visible={edit}
							onDismiss={() => setEdit(false)}
							contents={
								<Modal>
									<Modal.Header title="Modules bewerken" onClose={() => setEdit(false)} />
									<WorkspaceFeatures visible={edit} onClose={() => setEdit(false)} />
								</Modal>
							}
						>
							<Button
								type="secondary"
								icon={<GearIcon />}
								title="Bewerk modules"
								onClick={() => setEdit(true)}
							/>
						</Popover.Target>
					) : undefined
				}
			/>
			<Container mt={`${tokens.gap.page}px`}>
				<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
					<ConnectorErrorSummary />
					{isEngagementOfficer && <EngagmentOfficerOverviewSettings />}
					{isHRBusinessPartner && <HRPBOverviewSettings />}
					{!isEngagementOfficer && !isHRBusinessPartner && <DefaultOverviewSettings />}
				</FlexGap>
			</Container>
		</>
	);
}
